import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, CalendarDays, BookOpen, TrendingUp, BedDouble, Settings as SettingsIcon, LogOut, HelpCircle, Shield, Menu, X, Bell, Users, Wallet, Receipt } from "lucide-react";
import PondokIcon from "./PondokIcon";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const APP_NAME = "PondokRaja";
const DEFAULT_SUBTITLE = "Meos Ambower Homestay";

export default function Sidebar() {
  const { user, logout, isOwner, isSuperAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [subtitle, setSubtitle] = useState(DEFAULT_SUBTITLE);
  const location = useLocation();

  // Auto-close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Fetch homestay name from settings (per-tenant dynamic subtitle)
  useEffect(() => {
    api.get("/settings")
      .then((r) => { if (r.data?.homestay_name) setSubtitle(r.data.homestay_name); })
      .catch(() => {});
  }, [user?.tenant_id]);

  const items = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-link-dashboard", ownerOnly: false },
    { to: "/bookings", label: "Bookings", icon: BookOpen, testid: "nav-link-bookings", ownerOnly: false },
    { to: "/invoices", label: "Invoice", icon: Receipt, testid: "nav-link-invoices", ownerOnly: false },
    { to: "/reminders", label: "Reminder H-1", icon: Bell, testid: "nav-link-reminders", ownerOnly: false },
    { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-link-calendar", ownerOnly: false },
    { to: "/guests", label: "Database Tamu", icon: Users, testid: "nav-link-guests", ownerOnly: true },
    { to: "/expenses", label: "Pengeluaran", icon: Wallet, testid: "nav-link-expenses", ownerOnly: true },
    { to: "/reports", label: "Reports", icon: TrendingUp, testid: "nav-link-reports", ownerOnly: true },
    { to: "/rooms", label: "Kamar", icon: BedDouble, testid: "nav-link-rooms", ownerOnly: false },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-link-settings", ownerOnly: true },
    { to: "/help", label: "Bantuan", icon: HelpCircle, testid: "nav-link-help", ownerOnly: false },
  ];
  const visible = items.filter((i) => !i.ownerOnly || isOwner);

  const navItemCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${
      isActive
        ? "bg-[#D16B4D] text-white shadow-md shadow-[#D16B4D]/20"
        : "text-[#FDFBF7]/75 hover:text-white hover:bg-[#0D3322]"
    }`;

  return (
    <>
      {/* Mobile top bar (visible < lg) */}
      <div
        data-testid="mobile-topbar"
        className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-[#082618] text-[#FDFBF7] flex items-center justify-between px-4 py-3 shadow-md"
      >
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-[#D16B4D] flex items-center justify-center flex-shrink-0">
            <PondokIcon className="h-5 w-5 text-white" strokeWidth={1.8} />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-sm font-semibold">{APP_NAME}</div>
            <div className="text-[10px] text-[#FDFBF7]/60 font-medium">{subtitle}</div>
          </div>
        </div>
        <button
          data-testid="btn-menu-toggle"
          onClick={() => setOpen(true)}
          className="h-10 w-10 rounded-lg hover:bg-[#0D3322] flex items-center justify-center transition-colors"
          aria-label="Buka menu"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Backdrop (mobile drawer) */}
      {open && (
        <div
          data-testid="drawer-backdrop"
          onClick={() => setOpen(false)}
          className="lg:hidden fixed inset-0 z-30 bg-black/50 backdrop-blur-sm"
        />
      )}

      {/* Sidebar / Drawer */}
      <aside
        data-testid="sidebar"
        className={`fixed left-0 top-0 h-screen w-72 flex flex-col bg-[#082618] text-[#FDFBF7] p-6 gap-6 z-40 transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        {/* Brand */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#D16B4D] flex items-center justify-center shadow-lg shadow-[#D16B4D]/20 flex-shrink-0">
              <PondokIcon className="h-6 w-6 text-white" strokeWidth={1.8} />
            </div>
            <div className="leading-tight min-w-0">
              <div data-testid="sidebar-tenant-name" className="font-heading text-base font-semibold tracking-tight truncate">
                {APP_NAME}
              </div>
              <div className="text-xs text-[#FDFBF7]/60 font-medium">{subtitle}</div>
            </div>
          </div>
          {/* Close (mobile only) */}
          <button
            data-testid="btn-menu-close"
            onClick={() => setOpen(false)}
            className="lg:hidden h-9 w-9 rounded-lg hover:bg-[#0D3322] flex items-center justify-center flex-shrink-0"
            aria-label="Tutup menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 flex flex-col gap-1 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FDFBF7]/40 px-3 mb-2 font-bold">Menu</div>
          {visible.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} data-testid={testid} className={navItemCls}>
              <Icon className="h-4 w-4" strokeWidth={1.8} />
              {label}
            </NavLink>
          ))}

          {isSuperAdmin && (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FDFBF7]/40 px-3 mt-4 mb-2 font-bold">Platform</div>
              <NavLink to="/admin" data-testid="nav-link-admin" className={navItemCls}>
                <Shield className="h-4 w-4" strokeWidth={1.8} />
                Super Admin
              </NavLink>
            </>
          )}
        </nav>

        <div className="border-t border-white/10 pt-4 flex flex-col gap-3">
          <div className="flex items-center gap-3 px-2">
            <div className="h-9 w-9 rounded-full bg-[#D16B4D]/20 flex items-center justify-center font-heading font-semibold text-[#D16B4D]">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0" data-testid="sidebar-user-info">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[10px] text-[#FDFBF7]/60 uppercase tracking-wider font-bold">{user?.role}</div>
            </div>
          </div>
          <button
            data-testid="btn-logout"
            onClick={logout}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-[#FDFBF7]/75 hover:text-white hover:bg-[#0D3322] transition-colors"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.8} />
            Keluar
          </button>
        </div>
      </aside>
    </>
  );
}
